import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { useAnimation } from 'framer-motion6';
import { RiArrowRightUpLine } from 'react-icons/ri';
import {
  Content,
  Heading,
  Section,
  MissionText,
  MissionWrapper,
  SpotifyPlayer,
  OpenRolesButton
} from './styled';

const MISSION_HEIGHT = 620;

const Mission = ({ windowHeight, isMobile, allowScroll }) => {
  const topRootMargin = (windowHeight - MISSION_HEIGHT - 100) / 2;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    rootMargin: `-${topRootMargin >= 0 ? topRootMargin : 0}px 0px 0px 0px`
  });
  const [isInitial, setIsInitial] = useState(true);
  const headingControls = useAnimation();
  const contentControls = useAnimation();

  useEffect(() => {
    setTimeout(() => {
      setIsInitial(false);
    }, 4000);
  }, []);

  useEffect(() => {
    if (inView) {
      if (isInitial) {
        contentControls.start('mount');
        headingControls.start('mount');
      } else {
        contentControls.start('visible');
        headingControls.start('visible');
      }
    } else {
      headingControls.start('hidden');
      contentControls.start('hidden');
    }
  }, [inView]);

  const goToOpenRoles = () => {
    window.open('https://safetywing.pinpointhq.com', '_blank');
  };

  const heading = {
    hidden: { opacity: 0, y: 0 },
    mount: {
      opacity: [0, 1, 1, 1],
      y: [200, 180, 180, 0],
      transition: { delay: 1.6, duration: 1.8, times: [0, 0.3, 0.7, 1] }
    },
    visible: {
      opacity: [0, 1],
      y: [20, 0],
      transition: { duration: 0.6 }
    }
  };

  const content = {
    hidden: { opacity: 0 },
    mount: {
      opacity: 1,
      transition: { when: 'beforeChildren', delay: 2.9, staggerChildren: 0.2 }
    },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 0 },
    mount: {
      opacity: [0, 1],
      y: [20, 0],
      transition: { duration: 0.4 }
    },
    visible: {
      opacity: [0, 1],
      y: [20, 0],
      transition: { duration: 0.4 }
    }
  };

  return (
    <MissionWrapper ref={ref}>
      <Content>
        <Heading
          custom={isInitial}
          initial={!isMobile && 'hidden'}
          animate={!isMobile && headingControls}
          variants={heading}
        >
          Join us in building a country on the internet
        </Heading>
        <Section
          initial={!isMobile && 'hidden'}
          custom={isInitial}
          animate={!isMobile && contentControls}
          variants={content}
          onAnimationComplete={() => allowScroll()}
        >
          <MissionText variants={item}>
            Our mission is to remove the role of geographical borders as a barrier to
            equal opportunities and freedom for everyone. We are building a not-so-distant
            future where anyone can access things like healthcare, pension plans and even
            a passport — regardless of where they were born. If that gets you excited, we
            want to meet you!
          </MissionText>
          <SpotifyPlayer
            variants={item}
            title="Spotify - The story of SafetyWing"
            src="https://open.spotify.com/embed/episode/0EzkeItD2NyXEZjc1YuWsB?utm_source=generator"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          />
          <OpenRolesButton onClick={goToOpenRoles} variants={item}>
            <span>See open roles</span>
            <RiArrowRightUpLine size={22} />
          </OpenRolesButton>
        </Section>
      </Content>
    </MissionWrapper>
  );
};

Mission.propTypes = {
  windowHeight: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  allowScroll: PropTypes.func.isRequired
};

export default Mission;
